import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useCallback, useRef } from 'react';
import React from 'react';
import useCMStyles from './useCMStyles';
import { Box, Grid, IconButton, Paper, Typography } from '@material-ui/core';
import { getActiveInitRoute, getPermissions } from '../../utils/permissions';
import usePagination from '../../hooks/usePagination';
import { getAllTeamLeads } from '../../store/actions/cmActionCreator';
import { CM_STATUS_TYPES_MAPPING, preProcessPayload } from './helper.cm';
import { replaceWithUnderscore } from '../control-center/helper';
import useCommonStyles from '../../components/UI/CommonStyles';
import clsx from 'clsx';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import CustomDropdown from '../../components/Dropdown';
import { TrackingEnabledIcon } from '../../assets/svg';
import moment from 'moment';

interface User {
    name: string;
}

const useCM = (props: any) => {
    //values
    const containerRef = React.useRef<HTMLElement>(null);
    const initialPage = 0; // Start on page 0 (0-based index)
    const initialPageSize = 25; // Show 25 rows per page
    const initialActiveFilterCard = {
        type: 'Total',
        subType: '',
    }; // Example filter card
    const initialFilterValues = {}; // Example filter values
    const initialSearchText = ''; // Example search text
    const initialPayload = {
        pageNo: initialPage,
        pageSize: initialPageSize,
        searchText: '',
        filters: {
            ...initialFilterValues,
        },
        //status: 'SIGNED IN',
    };

    //State values
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [containerHeight, setContainerHeight] = useState(0);
    const [teamLeads, setTeamLeads] = useState<any>({});
    const [addTeamLeadModalOpen, setAddTeamLeadModalOPen] =
        useState<boolean>(false);

    //hooks
    const {
        pagination,
        handleChangePage,
        handleChangeRowsPerPage,
        activeFilterCard,
        filterValues,
        searchTextValue,
        payload,
        onFilterApplied,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        handleActiveFilterCard,
    } = usePagination(
        initialPage,
        initialPageSize,
        initialActiveFilterCard,
        initialFilterValues,
        initialSearchText,
        initialPayload,
    );
    const { user, cm, staff, settings } = useSelector(
        (store: DefaultRootState | any) => store || {},
    );
    const dispatch = useDispatch();

    // Memoize the dispatch function to prevent recreating it on every render
    const dispatchGetTeamLeads = useCallback(
        (data: any, onSuccess: Function, onError: Function) => {
            dispatch(getAllTeamLeads(data, onSuccess, onError));
        },
        [dispatch],
    );

    const styles: any = useCMStyles({
        containerHeight: containerHeight,
    });
    const commonStyles: any = useCommonStyles();

    // Create a ref to track the initial render
    const initialRender = useRef(true);

    useEffect(() => {
        let permissions = getPermissions('Cluster Management');
        if (!permissions.read) {
            props.history.push(`/${getActiveInitRoute()}`);
        }
    }, [user?.configs, props?.history]);

    useEffect(() => {
        if (!!containerRef?.current?.offsetHeight && !containerHeight) {
            const height = containerRef.current.offsetHeight;
            setContainerHeight(height);
        }
    }, [containerRef?.current?.offsetHeight, containerHeight]);

    // Memoize the API call function
    const fetchTeamLeads = useCallback(() => {
        setIsLoading(true);
        let preProceedPayload = preProcessPayload({
            ...payload,
            status: activeFilterCard?.status,
        });
        dispatchGetTeamLeads(
            preProceedPayload,
            (data: any) => {
                //setTeamLeads(data);
                setIsLoading(false);
            },
            (error: any) => {
                console.error('Error fetching team leads:', error);
                setIsLoading(false);
            },
        );
    }, [payload, dispatchGetTeamLeads, activeFilterCard]);

    useEffect(() => {
        // Add debounce to prevent rapid API calls when payload changes
        const timer = setTimeout(() => {
            fetchTeamLeads();
        }, 300);

        return () => clearTimeout(timer);
    }, [fetchTeamLeads, props?.location?.state]); // Add props.location?.state as dependency

    const prepareFilterCard = (card: any) => {
        switch (card?.type) {
            default:
                return (
                    <>
                        <Typography className={styles.cardHeader}>
                            {card?.name}
                        </Typography>
                        <Typography className={styles.cardSubHeader}>
                            {cm?.teamLeads?.[card?.valueMappingKey] ?? '0'}
                        </Typography>
                    </>
                );
        }
    };

    const handleOpenAddTeamLeadModal = () => {
        setAddTeamLeadModalOPen(true);
    };

    const handleCloseAddTeamLeadModal = () => {
        setAddTeamLeadModalOPen(false);
        fetchTeamLeads();
    };

    const getColumnComponentValue = (
        row: any,
        column: any,
        handleAction: any,
    ) => {
        let compValue: any = (
            <Box>
                <Typography className={styles.tableValue}>
                    {row?.[column?.valueKey] ?? '-/-'}
                </Typography>
            </Box>
        );
        const classStatus: any = replaceWithUnderscore(row?.status)
            ?.toUpperCase()
            ?.trim();
        let chipClass: any =
            commonStyles?.[`${CM_STATUS_TYPES_MAPPING?.[classStatus]}`];
        let loginClass: any =
            classStatus === 'SIGNED_IN'
                ? commonStyles.activeTxt
                : styles.tableValue;
        switch (column?.mappingKey) {
            case 'id':
                compValue = (
                    <Box>
                        <Typography className={styles.tableValue}>
                            {row?.name ?? '-'}
                            {row?.trackEnabled && !row?.contactNumber && (
                                <span
                                    style={{
                                        marginRight: '.5rem',
                                        marginLeft: '.5rem',
                                    }}
                                >
                                    <TrackingEnabledIcon fontSize={16} />
                                </span>
                            )}
                        </Typography>
                        <Typography className={styles.tableSubValue}>
                            {row?.contactNumber ?? ''}
                            {!!row?.contactNumber && row?.trackEnabled && (
                                <span
                                    style={{
                                        marginRight: '.5rem',
                                        marginLeft: '.5rem',
                                    }}
                                >
                                    <TrackingEnabledIcon fontSize={16} />
                                </span>
                            )}
                        </Typography>
                    </Box>
                );
                break;
            case 'active_riders':
                compValue = (
                    <Box>
                        <Typography className={styles.tableValue}>
                            {`${row?.activeRidersCount ?? '0'} / ${
                                row?.totalRidersCount ?? '0'
                            }`}
                        </Typography>
                    </Box>
                );
                break;
            case 'login':
                compValue = (
                    <Box>
                        <Typography
                            className={clsx(styles.tableValue, loginClass)}
                        >
                            {row?.signInAt
                                ? moment(row?.signInAt).format('hh:mm A')
                                : '-'}
                            {row?.signInAt && row?.signOutAt
                                ? `- ${moment(row?.signOutAt).format(
                                      'hh:mm A',
                                  )}`
                                : !!row?.signInAt
                                ? '- Live'
                                : ''}
                        </Typography>
                        <Typography className={styles.tableSubValue}>
                            {row?.activeTime
                                ? row?.signOutAt
                                    ? moment(row?.signOutAt).diff(
                                          moment(row?.signInAt),
                                          'hour',
                                      ) +
                                      'hrs ' +
                                      (moment(row?.signOutAt).diff(
                                          moment(row?.signInAt),
                                          'minute',
                                      ) %
                                          60) +
                                      'mins'
                                    : moment().diff(
                                          moment(row?.signInAt),
                                          'hour',
                                      ) +
                                      'hrs ' +
                                      (moment().diff(
                                          moment(row?.signInAt),
                                          'minute',
                                      ) %
                                          60) +
                                      'mins'
                                : '--'}
                        </Typography>
                    </Box>
                );
                break;
            case 'ev_deployed':
                compValue = (
                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        justifyContent={'center'}
                        alignItems={'center'}
                        className={clsx(styles.cardCX)}
                        maxWidth={'fit-content'}
                    >
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            style={{
                                borderRight: 'solid 1px #ebedf8',
                                padding: '10px',
                            }}
                        >
                            <Typography
                                className={clsx(
                                    styles.tableValue,
                                    styles.tableValueBold,
                                )}
                            >
                                {row?.evdeploymentsThisMonth ?? '0'}
                            </Typography>
                        </Box>
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            style={{
                                borderRight: 'solid 1px #ebedf8',
                                padding: '10px',
                            }}
                        >
                            <Typography
                                className={clsx(
                                    styles.tableValue,
                                    styles.tableValueBold,
                                )}
                            >
                                {row?.evreturnsThisMonth ?? '0'}
                            </Typography>
                        </Box>
                        <Box
                            display={'flex'}
                            flexDirection={'column'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            style={{
                                padding: '10px',
                            }}
                        >
                            <Typography
                                className={clsx(
                                    styles.tableValue,
                                    styles.tableValueBold,
                                )}
                            >
                                {row?.evnetChangeThisMonth ?? '0'}
                            </Typography>
                        </Box>
                    </Box>
                );
                break;
            case 'status':
                compValue = row?.status ? (
                    <Box
                        display={'flex'}
                        flexDirection={'column'}
                        alignItems={'flex-start'}
                        justifyContent={'flex-start'}
                    >
                        <Box className={clsx([commonStyles.chip, chipClass])}>
                            <Typography
                                component={'span'}
                                className={commonStyles.chipText}
                            >
                                {row?.status ?? '-'}
                            </Typography>
                        </Box>
                        {row?.statusText && (
                            <Typography className={styles.tableSubValue}>
                                {row?.statusText ?? '-'}
                            </Typography>
                        )}
                    </Box>
                ) : (
                    '-'
                );
                break;
            case 'teamLeadAction':
                compValue = (
                    <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        {/* <IconButton
                            className={commonStyles.menu}
                            aria-label="more"
                            aria-controls="long-menu"
                            aria-haspopup="true"
                            onClick={() => {
                                !!handleAction && handleAction('more', row);
                            }}
                            color="primary"
                        >
                            <Typography component={'span'} color="primary">
                                {'Options'}
                            </Typography>
                            <MoreVertIcon />
                        </IconButton> */}
                        <Typography className={styles.tableValue}>
                            {actionDropDown(row)}
                        </Typography>
                    </Box>
                );
                break;

            default:
                break;
        }
        return compValue;
    };

    const prepareFilterOptions = () => {
        return [];
    };

    const handleRowClick = (e: any, val: any, index: number) => {
        handleSingleTeamLeadPage(val);
    };

    const actionDropDown = (val: any) => {
        let options: any = [];
        const detailsView = {
            item: 'View Riders',
            onSuccess: (e: any) => {
                e?.stopPropagation();
                e?.preventDefault();
                if (!!val?.userId) {
                    handleSingleTeamLeadPage(val);
                }
            },
            disabled: false,
        };
        const trackMapView = {
            item: 'Live Tracking',
            onSuccess: (e: any) => {
                e?.stopPropagation();
                e?.preventDefault();
                if (!!val?.userId) {
                    // handleSingleTeamLeadPage(val);
                    props?.history.push(`/map-view/tl-detail/${val?.userId}`, {
                        payload: {
                            id: val?.userId,
                            ...val,
                        },
                    });
                }
            },
            disabled: false,
        };
        options = [detailsView];
        if (!!val?.orgBrokerEntityId && !!val?.userInfoBrokerEntityId) {
            options.push(trackMapView);
        }
        return (
            <CustomDropdown
                title={'Options'}
                options={options}
                optionStyles={null}
                expandMoreButtonStyles={null}
                styledMenuItemStyles={commonStyles.menu}
                buttonProps={null}
                buttonStyles={null}
            />
        );
    };

    const handleSingleTeamLeadPage = (tl: any) => {
        props?.history?.push(`/cluster-management/details/${tl?.userId}`, {
            payload: tl,
        });
    };

    return {
        user,
        isLoading,
        styles,
        activeFilterCard,
        prepareFilterCard,
        containerRef,
        prepareFilterOptions,
        searchTextValue,
        filterValues,
        onFilterApplied,
        pagination,
        handleChangePage,
        handleChangeRowsPerPage,
        teamLeads,
        onBackendSearch,
        onSearchTextChange,
        onRemoveAll,
        getColumnComponentValue,
        cm,
        handleActiveFilterCard,
        handleRowClick,
        handleOpenAddTeamLeadModal,
        handleCloseAddTeamLeadModal,
        addTeamLeadModalOpen,
    };
};

export default useCM;
